import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, getCurrentInstance, nextTick } from 'vue';
import {
    LogisticsTopBar,
    StepNavigator,
    MontageTransformator,
    MontageTable,
    ParsingOverlay,
} from '@/modules/Logistics/components/index';
import {
    LogisticsStep,
    SpreadsheetData,
    SpreadsheetColumn,
    MontageListPrintData,
    MontageListFormData,
    MontageCreatePayload,
    MontageUpdatePayload,
    MontageMeta,
    MontageListItem as MontageListTableItem,
    MontageItemUpdatePayload,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import {
    parseCSVTextGeneric,
    buildSpreadsheetData,
    buildMontagePrintSpreadsheetData,
    formatDataForMontageAPI,
    buildMontageFormData,
    extractMontagePrintRowsAsItemPayloads,
    formatDateString,
    inferDayFromDeliveryDate,
    syncSpreadsheetToParsedData,
    mapExtractedColumnsToSpreadsheetColumns,
} from '@/modules/Logistics/helpers/LogisticsParserHelper';
import MontageList from '@/modules/Logistics/api/models/MontageList';
import MontageListItemModel from '@/modules/Logistics/api/models/MontageListItem';
import MontageListRepository from '@/modules/Logistics/api/repositories/MontageListRepository';
import { formatDateToDateString } from '@/helpers/LocaleHelper';
import Vehicle from '@/modules/Logistics/api/models/Vehicle';
import MontageTeam from '@/modules/Logistics/api/models/MontageTeam';
import VehicleRepository from '@/modules/Logistics/api/repositories/VehicleRepository';
import MontageTeamRepository from '@/modules/Logistics/api/repositories/MontageTeamRepository';

type ParsedData = { data: Record<string, any>[]; columns: SpreadsheetColumn[] };

// ============================================================================
// STATE
// ============================================================================

type ProcessAction = 'parse' | 'save' | 'delete' | 'print' | 'save-and-prepare-print';


export default /*#__PURE__*/_defineComponent({
  __name: 'Montages',
  setup(__props) {

const { proxy } = getCurrentInstance()!;

const showTransformator = ref(false);
const transformatorRef = ref<InstanceType<typeof MontageTransformator> | null>(null);
const searchQuery = ref('');

const currentStepIndex = ref(0);
const rawData = ref('');
const parsedData = ref<ParsedData>({ data: [], columns: [] });
const spreadsheetData = ref<SpreadsheetData | null>(null);
const printData = ref<MontageListPrintData>({
    spreadsheetData: null,
    formData: {
        date: '',
        vehicle: '',
        installer: '',
        otherInstallers: '',
        preparedBy: '',
        isPrinted: null,
    },
});
const isParsing = ref(false);
const isMontagesTableLoading = ref(false);

const editingMontageListId = ref<string | null>(null);
const isEditMode = computed(() => editingMontageListId.value !== null);

// ============================================================================
// COMPUTED
// ============================================================================

const montageTeams = computed(() => {
    return MontageTeamRepository.getAll();
});
const vehicles = computed(() => {
    return VehicleRepository.getAll();
});

const currentMontageList = computed(() => {
    if (!editingMontageListId.value) {
        return null;
    }
    return MontageListRepository.getById(editingMontageListId.value);
});

function canProceed(stepIndex: number): boolean {
    switch (stepIndex) {
        case 0:
            return rawData.value.trim().length > 0;
        case 1:
            return spreadsheetData.value !== null;
        case 2:
            return printData.value.spreadsheetData !== null;
        default:
            return false;
    }
}

function canGoBack(_stepIndex: number): boolean {
    return false;
}

const transformatorSteps = computed<LogisticsStep[]>(() => [
    {
        id: 0,
        title: 'Import',
        description: 'Paste data',
        isActive: currentStepIndex.value === 0,
        isCompleted: currentStepIndex.value > 0,
        canProceed: canProceed(0),
        proceedButtonText: 'Next',
        class: 'import-step',
        proceedIcon: 'arrow-right',
        action: 'parse',
        showAlternativeButton: false,
        canGoBack: canGoBack(0),
    },
    {
        id: 1,
        title: 'Edit',
        description: 'Review and fix',
        isActive: currentStepIndex.value === 1,
        isCompleted: currentStepIndex.value > 1,
        canProceed: canProceed(1),
        proceedButtonText: 'Next',
        class: 'import-step',
        proceedIcon: 'arrow-right',
        action: 'save-and-prepare-print',
        showAlternativeButton: false,
        canGoBack: canGoBack(1),
    },
    {
        id: 2,
        title: 'Print',
        description: 'Review and print',
        isActive: currentStepIndex.value === 2,
        isCompleted: currentStepIndex.value > 2,
        canProceed: canProceed(2),
        proceedButtonText: 'Print',
        alternativeButtonText: 'Save',
        class: 'import-step',
        proceedIcon: 'print',
        action: 'print',
        alternativeAction: 'save',
        showAlternativeButton: true,
        canGoBack: canGoBack(2),
    },
]);

const currentStep = computed(() => transformatorSteps.value[currentStepIndex.value] || null);

const montageListsForTable = computed<MontageListTableItem[]>(() => {
    const lists = MontageListRepository.getAll();
    return lists.map((list: MontageList) => ({
        id: parseInt(list.id) || Date.now(),
        date: formatDateToDateString(list.date),
        number: String(list.id),
        installer: list.installer || '',
        otherInstallers: list.otherInstallers || '',
        preparedBy: list.preparedBy || '',
        vehicle: list.vehicle || '',
        printed: list.isPrinted ? '✅' : '❌',
        weekday: inferDayFromDeliveryDate(list.date),
    }));
});

const topBarTitle = computed(() => {
    if (showTransformator.value) {
        return isEditMode.value ? (proxy.$t('Edit Montage List') as string) : (proxy.$t('New Montage List') as string);
    }
    return proxy.$t('Montages') as string;
});

const topBarSubtitle = computed(() => {
    if (showTransformator.value && transformatorRef.value) {
        return transformatorRef.value.currentStepSubtitle;
    }
    return `${montageListsForTable.value.length} ${proxy.$t('montage lists')}`;
});

// ============================================================================
// LIFECYCLE
// ============================================================================

(() => {
    fetchMontageLists();
    fetchDependencies();
})();

// ============================================================================
// API METHODS
// ============================================================================

async function fetchMontageLists() {
    try {
        isParsing.value = true;
        await MontageList.fetchAll();
    } catch (error) {
        console.error('Error fetching montage lists:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to fetch montage lists.') as string,
        });
    } finally {
        isParsing.value = false;
    }
}

async function fetchDependencies() {
    try {
        await Promise.all([Vehicle.fetchAll(), MontageTeam.fetchAll()]);
    } catch (error) {
        console.error('Error fetching dependencies:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to fetch necessary data.') as string,
        });
    }
}

async function createMontageList(): Promise<MontageList | null> {
    try {
        const payload = buildMontageCreatePayload();
        const list = await MontageList.createNew(payload);
        proxy.$notification.success({
            message: proxy.$t('Success') as string,
            description: proxy.$t('Montage list created successfully.') as string,
        });
        return list;
    } catch (error) {
        console.error('Error creating montage list:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to create montage list.') as string,
        });
        return null;
    }
}

async function updateMontageList(): Promise<MontageList | null> {
    if (!editingMontageListId.value) {
        return null;
    }

    try {
        const payload = buildMontageUpdatePayload();

        await updateMontageItemsForList(payload.data);

        const list = await MontageList.updateExisting(editingMontageListId.value, payload.meta);

        return list;
    } catch (error) {
        console.error('Error updating montage list:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to update montage list.') as string,
        });
        return null;
    }
}

async function updateMontageItemsForList(printRowPayloads: MontageItemUpdatePayload[]): Promise<void> {
    if (!editingMontageListId.value || !currentMontageList?.value?.items?.length) {
        return;
    }

    const existingItems: MontageListItemModel[] = currentMontageList.value.items;
    const printRowPositions = new Set(printRowPayloads.map((row) => row.position));

    const itemsToDelete = existingItems.filter((item) => !printRowPositions.has(item.position));

    const itemsToUpdate = printRowPayloads.filter((payload) =>
        existingItems.some((item) => item.position === payload.position),
    );

    try {
        const deletePromises = itemsToDelete.map((item) => MontageListItemModel.deleteExisting(item.id));

        const updatePromises = itemsToUpdate.map((payload) => {
            const existingItem = existingItems.find((item) => item.position === payload.position)!;
            return MontageListItemModel.updateExisting(existingItem.id, payload);
        });

        await Promise.all([...deletePromises, ...updatePromises]);
    } catch (error) {
        console.error('Error updating montage list items:', error);
        throw error;
    }
}

async function processDeletingMontageLists(ids: string[]): Promise<void> {
    try {
        isMontagesTableLoading.value = true;
        await Promise.all(ids.map((id) => MontageList.deleteExisting(id)));
        proxy.$notification.success({
            message: proxy.$t('Success') as string,
            description: `${ids.length} ${proxy.$t('montage list(s) deleted.') as string}`,
        });
    } catch (error) {
        console.error('Error deleting montage lists:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to delete montage lists.') as string,
        });
    } finally {
        isMontagesTableLoading.value = false;
    }
}

async function processPrint() {
    if (printData.value.formData) {
        printData.value.formData.isPrinted = true;
    }

    await processSave();
    await printPDFData();

    if (printData.value.formData) {
        printData.value.formData.isPrinted = false;
    }
}

async function printPDFData() {
    if (editingMontageListId.value) {
        isParsing.value = true;
        try {
            const pdfBlob = await MontageList.getPDF(editingMontageListId.value);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank');
        } catch (e) {
            proxy.$notification.error({
                message: proxy.$t('Error') as string,
                description: proxy.$t('Failed to load PDF') as string,
            });
        } finally {
            isParsing.value = false;
        }
    }
}

// ============================================================================
// PAYLOAD BUILDERS
// ============================================================================

function buildMontageMeta(): MontageMeta {
    const formData = printData.value.formData;
    const formattedFormData: MontageMeta = {
        date: formatDateString(formData?.date || ''),
        vehicle: formData?.vehicle || '',
        installer: formData?.installer || '',
        otherInstallers: formData?.otherInstallers || '',
        preparedBy: formData?.preparedBy || '',
    };

    if (formData?.isPrinted === true || formData?.isPrinted === false) {
        formattedFormData.isPrinted = formData.isPrinted;
    }

    return formattedFormData;
}

function buildMontageCreatePayload(): MontageCreatePayload {
    parsedData.value.data = syncSpreadsheetToParsedData(spreadsheetData.value, parsedData.value.columns);
    return {
        data: formatDataForMontageAPI(parsedData.value.data),
        meta: buildMontageMeta(),
    };
}

function buildMontageUpdatePayload(): MontageUpdatePayload {
    return {
        data: extractMontagePrintRowsAsItemPayloads(
            printData.value.spreadsheetData,
            currentMontageList.value?.items || [],
        ),
        meta: buildMontageMeta(),
    };
}

// ============================================================================
// PROCESS DATA
// ============================================================================

async function processData(action: ProcessAction, payload?: any): Promise<boolean> {
    switch (action) {
        case 'parse':
            return processParse();

        case 'save':
            return await processSave();

        case 'delete':
            if (payload?.ids) {
                await processDeletingMontageLists(payload.ids.map(String));
            }
            return true;

        case 'print':
            await processPrint();
            return true;

        case 'save-and-prepare-print':
            return await saveMontageListAndPreparePrintData();

        default:
            return false;
    }
}

function processParse(): boolean {
    try {
        isParsing.value = true;

        const localParsedData = processRawData();

        if (!localParsedData) {
            return false;
        }

        parsedData.value = {
            data: localParsedData.data,
            columns: localParsedData.columns,
        };

        spreadsheetData.value = buildSpreadsheetData(parsedData.value.data, parsedData.value.columns);

        return true;
    } catch (error) {
        console.error('Error processing data:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t(
                'An error occurred while processing the data. Please check your input and try again.',
            ) as string,
        });
        return false;
    } finally {
        isParsing.value = false;
    }
}

async function processSave(): Promise<boolean> {
    try {
        isParsing.value = true;

        const list = isEditMode.value ? await updateMontageList() : await createMontageList();

        if (list) {
            editingMontageListId.value = String(list.id);
            MontageList.fetchAll();
            return true;
        }

        return false;
    } finally {
        isParsing.value = false;
    }
}

async function saveMontageListAndPreparePrintData(): Promise<boolean> {
    if (!parsedData.value.data.length) {
        return false;
    }

    const saved = await processSave();

    if (!saved) {
        return false;
    }

    await nextTick();

    return buildPrintDataForCurrentMontageList();
}

function buildPrintDataForCurrentMontageList(): boolean {
    if (!editingMontageListId.value) {
        return false;
    }

    if (!currentMontageList.value) {
        return false;
    }

    printData.value.spreadsheetData = buildMontagePrintSpreadsheetData(currentMontageList.value);
    printData.value.formData = buildMontageFormData(currentMontageList.value);

    return true;
}

// ============================================================================
// STEP NAVIGATION
// ============================================================================

async function handleNext(action?: string) {
    if (!action) {
        return;
    }

    switch (action) {
        case 'parse':
            const parsed = await processData('parse');
            if (parsed) {
                currentStepIndex.value = 1;
            }
            break;

        case 'save-and-prepare-print':
            if (await processData('save-and-prepare-print')) {
                currentStepIndex.value = 2;
            }
            break;
        case 'save':
            await processData('save');
            break;
        case 'print':
            processData('print');
            break;
    }
}

function handleBack() {
    if (currentStepIndex.value > 0) {
        currentStepIndex.value--;
    }

    if (currentStepIndex.value === 0) {
        resetData();
    }

    if (currentStepIndex.value === 1) {
        resetPrintData();
    }
}

function handleStepClick(_step: LogisticsStep, _stepIndex: number) {
    return;
}

// ============================================================================
// DATA PROCESSING HELPERS
// ============================================================================

function processRawData(): ParsedData | null {
    if (!rawData.value.trim()) {
        return null;
    }

    const result = parseCSVTextGeneric(rawData.value);

    if (!result.success) {
        throw new Error(proxy.$t('No valid data found in the input. Please check the format and try again.') as string);
    }

    return {
        data: result.data,
        columns: result.columns || mapExtractedColumnsToSpreadsheetColumns([]),
    };
}

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

function resetData() {
    rawData.value = '';
    parsedData.value = { data: [], columns: [] };
    spreadsheetData.value = null;
    resetPrintData();
}

function resetPrintData() {
    printData.value = {
        spreadsheetData: null,
        formData: {
            date: '',
            vehicle: '',
            installer: '',
            otherInstallers: '',
            preparedBy: '',
            isPrinted: null,
        },
    };
}

function resetTransformator() {
    currentStepIndex.value = 0;
    editingMontageListId.value = null;
    resetData();
}

// ============================================================================
// VIEW HANDLERS
// ============================================================================

function handleNewMontageList() {
    resetTransformator();
    showTransformator.value = true;
}

function handleBackToList() {
    showTransformator.value = false;
    resetTransformator();
}

function handleRowClick(record: MontageListTableItem) {
    editingMontageListId.value = String(record.id);

    buildPrintDataForCurrentMontageList();

    showTransformator.value = true;
    currentStepIndex.value = 2;
}

function handleDeleteRows(ids: number[]) {
    processData('delete', { ids });
}

// ============================================================================
// EVENT HANDLERS FROM TRANSFORMATOR
// ============================================================================

function handleDataPasted(data: string) {
    rawData.value = data;
}

function handleDataChange(data: SpreadsheetData | null) {
    if (data) {
        spreadsheetData.value = data;
    }
}

function handleFormFieldUpdate(
    field: keyof MontageListFormData,
    value: MontageListFormData[keyof MontageListFormData],
) {
    if (printData.value.formData) {
        (printData.value.formData[field] as MontageListFormData[typeof field]) = value;
    }
}

function handlePrintSpreadsheetChange(data: SpreadsheetData | null) {
    printData.value.spreadsheetData = data;
}

return { __sfc: true,proxy, showTransformator, transformatorRef, searchQuery, currentStepIndex, rawData, parsedData, spreadsheetData, printData, isParsing, isMontagesTableLoading, editingMontageListId, isEditMode, montageTeams, vehicles, currentMontageList, canProceed, canGoBack, transformatorSteps, currentStep, montageListsForTable, topBarTitle, topBarSubtitle, fetchMontageLists, fetchDependencies, createMontageList, updateMontageList, updateMontageItemsForList, processDeletingMontageLists, processPrint, printPDFData, buildMontageMeta, buildMontageCreatePayload, buildMontageUpdatePayload, processData, processParse, processSave, saveMontageListAndPreparePrintData, buildPrintDataForCurrentMontageList, handleNext, handleBack, handleStepClick, processRawData, resetData, resetPrintData, resetTransformator, handleNewMontageList, handleBackToList, handleRowClick, handleDeleteRows, handleDataPasted, handleDataChange, handleFormFieldUpdate, handlePrintSpreadsheetChange, LogisticsTopBar, StepNavigator, MontageTransformator, MontageTable, ParsingOverlay }
}

})